import { action, computed, flow, makeObservable, observable } from 'mobx';

import { BaseApiError } from 'src/errors/errors';
import type { TOption } from 'src/features/well-form/types';
import { debounce } from 'src/shared/utils/debounce';
import type { TPlanVersion } from 'src/store/comparison/types';
import type { Directories } from 'src/store/directories/directories.store';
import type { NotificationsStore } from 'src/store/notifications-store/notifications-store';
import { GOPLAN_DRILLING_CARPET_PREPARATION_ACCESS_EDIT } from 'src/store/roles-store/roles';
import type { RolesStore } from 'src/store/roles-store/roles-store';
import type { WellPadPreparationService } from 'src/store/well-pad-preparation';
import type { PadPreparation } from 'src/store/well-pad-preparation/entities';

import type { PAD_DRILLING_STATUS } from './consts';
import { WellPadPreparationFilterStore } from './well-pad-preparation-filter.store';

export type TPadDrillingStatus = keyof typeof PAD_DRILLING_STATUS;

interface IPlanVersionService {
  publicDraft: TPlanVersion | null;
  currentVersion: TPlanVersion | null;
  isDraftOfPublicDraftVersion(version: number | TPlanVersion): boolean;
}

export class WellPadPreparationStore {
  private readonly notifications: NotificationsStore;
  private readonly planVersion: IPlanVersionService;
  private readonly wellPadPreparationService: WellPadPreparationService;
  private readonly directoriesService: Directories;
  private readonly rolesStore: RolesStore;

  readonly filterStore: WellPadPreparationFilterStore = new WellPadPreparationFilterStore();

  @observable currentPadStatusToDisplay: TPadDrillingStatus = 'planned';
  @observable searchValue: string = '';
  @observable isFilterModalOpened = false;
  @observable editingPadId: number | null = null;

  constructor(
    directoriesService: Directories,
    planVersionsService: IPlanVersionService,
    wellPadPreparationService: WellPadPreparationService,
    rolesStore: RolesStore,
    notifications: NotificationsStore,
  ) {
    this.notifications = notifications;
    this.planVersion = planVersionsService;
    this.wellPadPreparationService = wellPadPreparationService;
    this.directoriesService = directoriesService;
    this.rolesStore = rolesStore;

    makeObservable(this);
  }

  @computed
  private get getSearchFilterFn(): (pad: PadPreparation) => boolean {
    const searchValue = this.searchValue;

    return (pad) => {
      const {
        constantValues: { padName, blockName, fieldName },
      } = pad;

      return padName.includes(searchValue) || blockName.includes(searchValue) || fieldName.includes(searchValue);
    };
  }

  @computed
  private get getFilterFnByPadStatus(): (pad: PadPreparation) => boolean {
    const getPadStatus = (pad: PadPreparation): TPadDrillingStatus => {
      if (!pad.rigSiteEvents.length || (pad.rigSiteEvents.length === 1 && pad.rigSiteEvents[0].id === 'blank')) {
        return 'unplanned';
      }

      if (!!pad.constantValues.drilled) {
        return 'drilled';
      }

      return 'planned';
    };

    return (pad) => {
      if (this.currentPadStatusToDisplay === 'updated') {
        return !!pad.values.isChanged;
      }

      return this.currentPadStatusToDisplay === getPadStatus(pad);
    };
  }

  private _updateSearchValue(searchValue: string): void {
    this.searchValue = searchValue;
  }

  @computed
  private get sortedPadPreparations(): PadPreparation[] {
    return [...this.wellPadPreparationService.pads].sort((a, b) => {
      const { blockName: blockNameA, fieldName: fieldNameA, padName: padNameA } = a.constantValues;
      const { blockName: blockNameB, fieldName: fieldNameB, padName: padNameB } = b.constantValues;

      const blockCompRes = blockNameA.localeCompare(blockNameB);
      const fieldCompRes = fieldNameA.localeCompare(fieldNameB) / 10;
      const padNameCompRes = padNameA.localeCompare(padNameB) / 100;

      return blockCompRes + fieldCompRes + padNameCompRes;
    });
  }

  @computed
  get updatedPadsCount(): number {
    return this.sortedPadPreparations.filter((pad) => pad.values.isChanged).length;
  }

  @computed
  get noResultReason(): 'noData' | 'noDataByFilters' | null {
    if (this.pads.length) {
      return null;
    }

    if (!this.wellPadPreparationService.pads.length) {
      return 'noData';
    }

    return 'noDataByFilters';
  }

  @computed
  get padTypeOptions(): TOption<number>[] {
    return (
      this.directoriesService
        .getObject('Common_PadType')
        ?.map((d) => ({ label: d.data['name'].toString(), value: d.id as number })) ?? []
    );
  }

  @computed
  get passageTypeOptions(): TOption<number>[] {
    return (
      this.directoriesService
        .getObject('Common_PassageType')
        ?.map((d) => ({ label: d.data['name'].toString(), value: d.id as number })) ?? []
    );
  }

  @computed
  get pads(): PadPreparation[] {
    return this.sortedPadPreparations.filter(
      (pad) => this.getFilterFnByPadStatus(pad) && this.getSearchFilterFn(pad) && this.filterStore.filterFn(pad),
    );
  }

  @computed
  get isEditModeAvailable(): boolean {
    const currentVersion = this.planVersion.currentVersion;

    if (!currentVersion) {
      return false;
    }

    if (!this.rolesStore.hasAccess([GOPLAN_DRILLING_CARPET_PREPARATION_ACCESS_EDIT])) {
      return false;
    }

    return currentVersion === this.planVersion.publicDraft;
  }

  @computed
  get isLoading(): boolean {
    return this.wellPadPreparationService.isDataFetching || !this.wellPadPreparationService.isInitialized;
  }

  @action.bound
  setEditingPadId(id: number | null): void {
    this.editingPadId = id;
  }

  @action.bound
  setIsFilterModalOpen(is: boolean): void {
    this.isFilterModalOpened = is;
  }

  @action.bound
  toggleFilterModal(): void {
    this.setIsFilterModalOpen(!this.isFilterModalOpened);
  }

  @action.bound
  setPadStatusToDisplay(status: TPadDrillingStatus): void {
    this.currentPadStatusToDisplay = status;
  }
  @flow.bound
  async *applyPadChanges(pad: PadPreparation) {
    try {
      await this.wellPadPreparationService.applyPadChanges(pad);
      yield;

      this.setEditingPadId(null);
    } catch (e) {
      console.error(e);
      if (e instanceof BaseApiError && e.responseMessage) {
        this.notifications.showErrorMessageT(e.responseMessage);
      }
    }
  }

  setSearchValue = debounce(action(this._updateSearchValue.bind(this)), 300);
}
